import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "lumir-back-office-ui";
import { InputField } from "./inputField";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import styled from "styled-components";

// styled-components로 구현한 드롭다운 메뉴 아이템
const MenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1.3rem;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const IconContainer = styled.span`
  width: 10px;
  margin-right: 0.5rem;
`;

const MenuItemText = styled.span`
  font-size: 1.5rem;
  font-weight: 400;
  line-height: 1.5;
  color: #333;
  font-family: var(--font-pretendard), system-ui, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.7rem;
  }
`;

const StyledDropdownContent = styled(DropdownMenuContent)`
  max-height: 400px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 0.375rem;
  width: ${(props) => `${props.$width}` || "auto"};
`;

const CustomDropdownMenuItem = ({ children, selected, onClick }) => {
  return (
    <MenuItem onClick={onClick} className="custom-cursor">
      {selected ? (
        <Check
          size={20}
          style={{
            marginRight: "0.25rem",
            color: "var(--semantic-primary-background-normal-default, #009FE9)",
          }}
        />
      ) : (
        <IconContainer />
      )}
      <MenuItemText>{children}</MenuItemText>
    </MenuItem>
  );
};

const SelectBox = ({
  placeholder,
  contents,
  label,
  contentClassName,
  error,
  disabled,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const iconRef = useRef(null);

  useEffect(() => {
    if (props.value) {
      const selectedItem = contents.find((item) => item.id === props.value);
      setSelected(selectedItem);
    }
  }, [props.value, contents]);

  const handleSelect = (item) => {
    if (!item || !contents) return;

    setSelected(item);
    if (props.onChange) {
      const event = {
        target: {
          value: item.id,
          name: props.name,
        },
      };
      props.onChange(event);
    }
    setOpen(false);
  };

  // Custom Icon component with tabIndex
  const CustomIcon = ({ icon: Icon }) => (
    <div
      ref={iconRef}
      tabIndex={0}
      style={{ display: "inline-flex", outline: "none" }}
    >
      <Icon className="text-semantic-secondary-foreground-strong-default" />
    </div>
  );

  // Extract width from contentClassName
  const extractWidth = () => {
    if (!contentClassName) return null;

    // Handling calc expressions directly
    if (contentClassName.includes("calc")) {
      // Extract the full calc expression
      const calcMatch = contentClassName.match(/w-\[(calc\(.*?\))\]/);
      if (calcMatch && calcMatch[1]) {
        // Add spaces around operators if they don't exist
        return calcMatch[1]
          .replace(/([+\-*/])(?!\s)/g, " $1 ")
          .replace(/\s{2,}/g, " ");
      }
      return null;
    }

    // For standard width values
    const match = contentClassName.match(/w-\[([^\]]+)\]/);
    return match ? match[1] : null;
  };

  return (
    <DropdownMenu
      className="w-full "
      open={open}
      onOpenChange={() => setOpen(!open)}
    >
      <DropdownMenuTrigger className="w-full" disabled={disabled}>
        <InputField
          label={label}
          placeholder={placeholder}
          value={selected?.name || ""}
          error={error}
          rightIcon={
            open ? (
              <CustomIcon icon={ChevronUp} />
            ) : (
              <CustomIcon icon={ChevronDown} />
            )
          }
          tabIndex={-1}
          disabled={disabled}
          {...props}
        />
      </DropdownMenuTrigger>
      <StyledDropdownContent $width={extractWidth()}>
        {contents.map((item) => (
          <CustomDropdownMenuItem
            key={item.id}
            selected={selected?.id === item.id}
            onClick={() => handleSelect(item)}
          >
            {item.name}
          </CustomDropdownMenuItem>
        ))}
      </StyledDropdownContent>
    </DropdownMenu>
  );
};

export { SelectBox };
